// Task Management Types for Frontend

export enum TaskStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  RESOLVED = 'RESOLVED',
  CLOSED = 'CLOSED',
}

export enum TaskPriority {
  LOW = 'LOW',
  NORMAL = 'NORMAL',
  HIGH = 'HIGH',
  URGENT = 'URGENT',
}

export enum TaskActor {
  FAIRFARE = 'FAIRFARE',
  NETWORK = 'NETWORK',
  CARRIER = 'CARRIER',
}

export enum FeedType {
  TEXT = 'TEXT',
  ATTACHMENT= 'ATTACHMENT',
  SYSTEM= 'SYSTEM'
}

export interface TaskTopic {
  id: number;
  name: string;
  description?: string | null;
  responsibleActor: TaskActor;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface TaskTenantsListForNetworkResponse {
  tenantId: number;
  tenants: {
    id: number;
    name: string;
    carriers: {
      id: number;
      name: string;
    }[];
  }[];
}

export interface TaskUser {
  id: number;
  fullName: string;
  email: string;
  role?: string;
}

export interface TaskFeedEntry {
  id: string;
  type: FeedType;
  message?: string | null;
  attachmentUrl?: string | null;
  attachmentName?: string | null;
  createdAt: Date;
  createdBy: TaskUser;
  metadata?: Record<string, any>;
}

export interface Task {
  id: string;
  title: string;
  description?: string | null;
  status: TaskStatus;
  type: string;
  priority: TaskPriority;
  dueDate?: Date | null;
  responsibleActor: TaskActor;
  tenantId: number;
  responsibleTenantId: number;
  caseId?: string | null;
  policyNumber?: string | null;
  claimId?: number | null;
  billId?: string | null;
  createdAt: Date;
  updatedAt: Date;
  createdBy: TaskUser;
  assignedTo?: TaskUser | null;
  topic?: TaskTopic | null;
  feedCount?: number;
}

export interface TaskDetail extends Task {
  feed: TaskFeedEntry[];
}

export interface TaskFilters {
  status?: TaskStatus | TaskStatus[];
  priority?: TaskPriority | TaskPriority[];
  responsibleActor?: TaskActor;
  assignedToUserId?: number;
  createdById?: number;
  caseId?: string;
  policyCaseId?: number;
  claimId?: number;
  billId?: string;
  overdue?: boolean;
  myTasks?: boolean;
  search?: string;
  hideResolvedClosed?: boolean;
}

export interface TaskPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface TasksResponse {
  tasks: Task[];
  pagination: TaskPagination;
}

export interface CreateTaskInput {
  title: string;
  topicId: number;
  topicCategory: string;
  topicDescription: string;
  additionalDescription: string;
  taskPriority: TaskPriority;
  caseIdentifier: string;
  caseIdentifierType?: 'caseId' | 'policyNumber';
  dueDate?: Date;
  carrierTenantId?: string;
}

export interface UpdateTaskInput {
  title?: string;
  description?: string;
  status?: TaskStatus;
  type?: string;
  priority?: TaskPriority;
  topicId?: number;
  dueDate?: Date;
  assignedToUserId?: number;
  responsibleActor?: TaskActor;
}

export interface CreateFeedEntryInput {
  type: FeedType;
  message?: string;
  attachmentUrl?: string;
  attachmentName?: string;
}

export interface CreateTopicInput {
  name: string;
  description?: string;
  responsibleActor: TaskActor;
}

export interface UpdateTopicInput {
  name?: string;
  description?: string;
  responsibleActor?: TaskActor;
  isActive?: boolean;
}

// Helper type for task statistics
export interface TaskStats {
  total: number;
  byStatus: Record<TaskStatus, number>;
  byPriority: Record<TaskPriority, number>;
  overdue: number;
  dueToday: number;
  dueThisWeek: number;
}
